import { useEffect, useRef, useState } from "react";

import {
    type LienWaiverInfo,
    type LienWaiverReviewResponse,
} from "entity/bill/LienWaiverReview/LienWaiverReview.api.types";
import { LienWaiverStatusTypes } from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";
import { OutboundPaymentsExternalHandler } from "entity/onlinePayment/OutboundPaymentsExternal/OutboundPaymentsExternal.api.handler";
import { PendingOutboundPaymentStatus } from "entity/onlinePayment/OutboundPaymentsExternal/OutboundPaymentsExternal.api.types";

/**
 * Query param naming the waiver a sub just signed through Nutrient. The signing flow appends it to the
 * return URL so the review page knows an approval is in flight for that row.
 */
export const SIGNED_LIEN_WAIVER_PARAM = "signedLienWaiverId";

const POLL_INTERVAL_MS = 5000;

/** The payout follows the approval within seconds, so it is checked more often than the approval. */
const PAYOUT_POLL_INTERVAL_MS = 3000;

/**
 * Bounded so a sub never sits on an endless spinner. Nutrient approval normally lands within a couple of
 * seconds; past this the page tells them the approval was received and is still processing.
 */
const POLL_TIMEOUT_MS = 60_000;

/** A waiver has left the sub's queue once it is no longer awaiting their signature. */
export function isLienWaiverStatusPending(status: LienWaiverStatusTypes | undefined) {
    return status === LienWaiverStatusTypes.Released;
}

export function isLienWaiverStillPending(lienWaiver: LienWaiverInfo | null | undefined) {
    return isLienWaiverStatusPending(lienWaiver?.status);
}

function findLienWaiver(data: LienWaiverReviewResponse | undefined, encodedLienWaiverId: string) {
    return data?.allBills
        .flatMap((bill) => bill.lienWaivers)
        .find((lienWaiver) => String(lienWaiver.lienWaiverId) === encodedLienWaiverId);
}

export type SignedLienWaiverPollState = "idle" | "polling" | "resolved" | "timedOut";

interface IUseSignedApprovalPollOptions {
    /** Identifies the signature in flight. Undefined when nothing is in flight. */
    pollKey: string | undefined;
    isStillPending: () => boolean;
    onRefresh: () => Promise<void>;
    intervalMs?: number;
}

/**
 * Waits for a Nutrient signature's approval to be processed.
 *
 * Approval happens asynchronously via pubsub after the signing completion, so data reloaded straight
 * after signing can still show the waiver as pending. This re-fetches on an interval until
 * `isStillPending` reports false, then stops. It also stops at a timeout or on a failed refresh, in
 * which case the caller tells the sub their approval is still processing rather than showing an error.
 */
export function useSignedApprovalPoll({
    pollKey,
    isStillPending,
    onRefresh,
    intervalMs = POLL_INTERVAL_MS,
}: IUseSignedApprovalPollOptions) {
    const [state, setState] = useState<SignedLienWaiverPollState>("idle");

    // Read inside the interval without making them dependencies — re-creating the interval on every
    // refreshed payload would restart the timer and the poll would never reach its deadline.
    const isStillPendingRef = useRef(isStillPending);
    const onRefreshRef = useRef(onRefresh);
    useEffect(() => {
        isStillPendingRef.current = isStillPending;
        onRefreshRef.current = onRefresh;
    });

    useEffect(() => {
        if (!pollKey) {
            setState("idle");
            return;
        }

        // Already resolved in the data the page loaded with — the approval beat us here.
        if (!isStillPendingRef.current()) {
            setState("resolved");
            return;
        }

        setState("polling");

        let cancelled = false;
        const deadline = Date.now() + POLL_TIMEOUT_MS;

        const intervalId = window.setInterval(() => {
            if (cancelled) {
                return;
            }

            void onRefreshRef
                .current()
                .then(() => {
                    if (cancelled) {
                        return;
                    }
                    if (!isStillPendingRef.current()) {
                        window.clearInterval(intervalId);
                        setState("resolved");
                        return;
                    }
                    if (Date.now() >= deadline) {
                        window.clearInterval(intervalId);
                        setState("timedOut");
                    }
                })
                .catch(() => {
                    if (cancelled) {
                        return;
                    }
                    window.clearInterval(intervalId);
                    setState("timedOut");
                });
        }, intervalMs);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [pollKey, intervalMs]);

    return state;
}

interface IUseSignedLienWaiverPollOptions {
    /** Encoded id of the waiver just signed, from the return URL. Undefined when nothing is in flight. */
    signedLienWaiverId: string | undefined;
    data: LienWaiverReviewResponse | undefined;
    onRefresh: () => Promise<void>;
}

/** Polls the review list until the waiver just signed leaves the pending state. */
export function useSignedLienWaiverPoll({
    signedLienWaiverId,
    data,
    onRefresh,
}: IUseSignedLienWaiverPollOptions) {
    return useSignedApprovalPoll({
        pollKey: signedLienWaiverId,
        isStillPending: () =>
            !!signedLienWaiverId &&
            isLienWaiverStillPending(findLienWaiver(data, signedLienWaiverId)),
        onRefresh,
    });
}

/** Statuses that still offer the sub a deposit choice, which an in-flight auto payout replaces. */
const PAYOUT_NOT_YET_SENT_STATUSES = [
    PendingOutboundPaymentStatus.PendingDepositSelection,
    PendingOutboundPaymentStatus.PendingLienWaivers,
];

const outboundPaymentsHandler = new OutboundPaymentsExternalHandler();

interface IUseAutoPayoutPollOptions {
    paymentShareToken: string | undefined;
    /** The payout only starts after the approval, so it is not checked until the approval lands. */
    isApprovalResolved: boolean;
    getPaymentStatus?: (shareToken: string) => Promise<PendingOutboundPaymentStatus>;
}

const getPaymentStatusDefault = async (shareToken: string) =>
    (await outboundPaymentsHandler.get(shareToken)).status;

/**
 * Waits for an auto payout to be sent once the releasing approval lands.
 *
 * The pubsub approval commits before the payout runs, so the payment reads as awaiting a deposit choice
 * for a few seconds after the waiver resolves. Sending the sub on in that gap shows them deposit options
 * for a payment that is already going out automatically.
 */
export function useAutoPayoutPoll({
    paymentShareToken,
    isApprovalResolved,
    getPaymentStatus = getPaymentStatusDefault,
}: IUseAutoPayoutPollOptions) {
    // A ref, not state: the poll reads the status in the same tick the fetch resolves, before any re-render.
    const statusRef = useRef<PendingOutboundPaymentStatus>();

    const pollKey = paymentShareToken && isApprovalResolved ? paymentShareToken : undefined;

    // Pending until the first check reports otherwise, so the poll never resolves on a status it has not read.
    const state = useSignedApprovalPoll({
        pollKey,
        isStillPending: () =>
            statusRef.current === undefined ||
            PAYOUT_NOT_YET_SENT_STATUSES.includes(statusRef.current),
        onRefresh: async () => {
            statusRef.current = await getPaymentStatus(pollKey!);
        },
        intervalMs: PAYOUT_POLL_INTERVAL_MS,
    });

    // The poll only leaves idle in an effect, so the render that starts it would otherwise read as done
    // and let a caller navigate before the first check.
    return pollKey && state === "idle" ? "polling" : state;
}
